import { useCallback, useState } from 'react';

import {
  generateFilingCalendar,
  GenerateFilingCalendarResult,
} from '@commenda/api/new-sales-tax/filing-calendar';
import { SalesTaxFilingFrequencyEnum } from '@prisma/client';

import { CalendarPreviewHelper } from '../helpers';
import { MonthYearValue } from '../types';

export interface UseFilingCalendarGenerateParams {
  corporationId: number;
  onSuccess?: (result: GenerateFilingCalendarResult) => void;
  onError?: (error: string) => void;
}

export interface GenerateCalendarParams {
  state: string;
  filingFrequency: SalesTaxFilingFrequencyEnum;
  taxPeriodStart: MonthYearValue;
  taxPeriodEnd?: MonthYearValue | null;
}

export interface UseFilingCalendarGenerateResult {
  isGenerating: boolean;
  generateError: string | null;
  generate: (params: GenerateCalendarParams) => Promise<boolean>;
  clearError: () => void;
}

export function useFilingCalendarGenerate({
  corporationId,
  onSuccess,
  onError,
}: UseFilingCalendarGenerateParams): UseFilingCalendarGenerateResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  const generate = useCallback(
    async (params: GenerateCalendarParams): Promise<boolean> => {
      const { state, filingFrequency, taxPeriodStart, taxPeriodEnd } = params;

      setIsGenerating(true);
      setGenerateError(null);

      try {
        const result = await generateFilingCalendar({
          corporationId,
          state,
          filingFrequency,
          taxPeriodStartMonth:
            CalendarPreviewHelper.monthYearToYYYYMM(taxPeriodStart),
          taxPeriodEndMonth: taxPeriodEnd
            ? CalendarPreviewHelper.monthYearToYYYYMM(taxPeriodEnd)
            : undefined,
        });

        if (result.success) {
          onSuccess?.(result);
          return true;
        }

        const errorMessage = result.error || 'Failed to generate calendar';
        setGenerateError(errorMessage);
        onError?.(errorMessage);
        return false;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred';
        setGenerateError(errorMessage);
        onError?.(errorMessage);
        return false;
      } finally {
        setIsGenerating(false);
      }
    },
    [corporationId, onSuccess, onError]
  );

  const clearError = useCallback(() => {
    setGenerateError(null);
  }, []);

  return {
    isGenerating,
    generateError,
    generate,
    clearError,
  };
}
